import { ClickAwayListener } from "@mui/material";
import { genericMemo } from "@redotech/react-util/component";
import {
  Alignment,
  Section,
  SocialPlatform,
} from "@redotech/redo-model/email-builder";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import AppleIcon from "@redotech/redo-web/arbiter-icon/social-icon-apple.svg";
import DiscordIcon from "@redotech/redo-web/arbiter-icon/social-icon-discord.svg";
import FacebookIcon from "@redotech/redo-web/arbiter-icon/social-icon-facebook.svg";
import GithubIcon from "@redotech/redo-web/arbiter-icon/social-icon-github.svg";
import GoogleIcon from "@redotech/redo-web/arbiter-icon/social-icon-google.svg";
import InstagramIcon from "@redotech/redo-web/arbiter-icon/social-icon-instagram.svg";
import LinkedInIcon from "@redotech/redo-web/arbiter-icon/social-icon-linkedin.svg";
import PinterestIcon from "@redotech/redo-web/arbiter-icon/social-icon-pinterest.svg";
import RedditIcon from "@redotech/redo-web/arbiter-icon/social-icon-reddit.svg";
import SnapchatIcon from "@redotech/redo-web/arbiter-icon/social-icon-snapchat.svg";
import TiktokIcon from "@redotech/redo-web/arbiter-icon/social-icon-tiktok.svg";
import TwitterIcon from "@redotech/redo-web/arbiter-icon/social-icon-twitter.svg";
import YoutubeIcon from "@redotech/redo-web/arbiter-icon/social-icon-youtube.svg";
import { memo, useMemo, useRef, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoInputSize } from "../../arbiter-components/input/base-redo-text-input";
import {
  WidgetEditorProps,
  WidgetProps,
} from "../../form-builder/widget-editors/widget-editor-decorator";
import { WidgetElementEditor } from "../../form-builder/widget-editors/widget-element-editor";

export const SocialsEditor = genericMemo(function SocialsEditor<
  T extends WidgetProps<Section.Socials>,
>(props: WidgetEditorProps<T, Section.Socials>) {
  const element = props.widget.element;
  const padding = element.sectionPadding;

  const getAlignment = () => {
    if (element.alignment === Alignment.LEFT) {
      return "flex-start";
    }
    if (element.alignment === Alignment.RIGHT) {
      return "flex-end";
    }
    return "space-between";
  };

  return (
    <WidgetElementEditor {...props} skipRenderingSectionToolbar>
      {(props) => (
        <>
          <div
            style={{
              backgroundColor: element.sectionColor,
              paddingBottom: padding.bottom,
              paddingLeft: padding.left,
              paddingRight: padding.right,
              paddingTop: padding.top,
              display: "flex",
              justifyContent: getAlignment(),
            }}
          >
            {element.socialLinks.map((item, index) => {
              return (
                <SocialsTile
                  color={element.iconColor}
                  iconPadding={element.iconPadding}
                  key={index}
                  platform={item.platform}
                  save={(url) => {
                    props.setElement({
                      ...element,
                      socialLinks: [
                        ...element.socialLinks.slice(0, index),
                        { ...item, url: url },
                        ...element.socialLinks.slice(index + 1),
                      ],
                    });
                  }}
                  url={item.url}
                />
              );
            })}
          </div>
          {props.sectionToolbar}
        </>
      )}
    </WidgetElementEditor>
  );
});

export const SocialsTile = memo(function SocialsTile(props: {
  platform: SocialPlatform;
  color: string;
  iconPadding: number;
  url: string;
  save: (url: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [url, setUrl] = useState(props.url);
  const Icon = useMemo(
    () => SocialsIcon({ width: 24, height: 24 })[props.platform],
    [props.platform],
  );
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div
      style={{
        display: "flex",
        position: "relative",
        padding: props.iconPadding,
      }}
    >
      <div
        onClick={() => setIsEditing(!isEditing)}
        style={{ color: props.color, cursor: "pointer" }}
      >
        {Icon}
      </div>
      {isEditing && (
        <ClickAwayListener
          mouseEvent="onMouseDown"
          onClickAway={(event) => {
            if (
              event.target instanceof Node &&
              ref.current?.contains(event.target)
            ) {
              return;
            }
            setIsEditing(false);
          }}
        >
          <div ref={ref}>
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1000,
                marginTop: "4px",
                minWidth: "200px",
                display: "flex",
                flexDirection: "row",
                gap: "4px",
                border: "1px solid #E0E0E0",
                borderRadius: "4px",
                padding: "4px",
                backgroundColor: "white",
              }}
            >
              <RedoTextInput
                placeholder="Paste or type a link"
                setValue={setUrl}
                size={RedoInputSize.SMALL}
                value={url}
              />
              <RedoButton
                onClick={() => {
                  props.save(url);
                  setIsEditing(false);
                }}
                text="Save"
              />
            </div>
          </div>
        </ClickAwayListener>
      )}
    </div>
  );
});

export const SocialsIcon = (props?: { width: number; height: number }) => {
  const iconProps = { height: props?.height, width: props?.width };

  const socialIcons: Record<SocialPlatform, JSX.Element> = {
    [SocialPlatform.APPLE]: <AppleIcon {...iconProps} />,
    [SocialPlatform.DISCORD]: <DiscordIcon {...iconProps} />,
    [SocialPlatform.FACEBOOK]: <FacebookIcon {...iconProps} />,
    [SocialPlatform.GITHUB]: <GithubIcon {...iconProps} />,
    [SocialPlatform.GOOGLE]: <GoogleIcon {...iconProps} />,
    [SocialPlatform.INSTAGRAM]: <InstagramIcon {...iconProps} />,
    [SocialPlatform.LINKEDIN]: <LinkedInIcon {...iconProps} />,
    [SocialPlatform.PINTEREST]: <PinterestIcon {...iconProps} />,
    [SocialPlatform.REDDIT]: <RedditIcon {...iconProps} />,
    [SocialPlatform.SNAPCHAT]: <SnapchatIcon {...iconProps} />,
    [SocialPlatform.TIKTOK]: <TiktokIcon {...iconProps} />,
    [SocialPlatform.TWITTER]: <TwitterIcon {...iconProps} />,
    [SocialPlatform.YOUTUBE]: <YoutubeIcon {...iconProps} />,
  };

  return socialIcons;
};
